import { useState } from "react";
import { toast } from "sonner";
import { Trash2, Plus, Wand2, UserPlus, ChevronsUpDown, BookOpen, Save, Shuffle, RefreshCw } from "lucide-react";
import { uid, autoGroups, shuffleIds } from "@/lib/constants";
import { initials, dayHandicap } from "@/lib/scoring";
import { createLibraryCourse, createProfile, searchProfiles } from "@/lib/api";
import CourseLibraryModal from "@/screens/CourseLibraryModal";
import PlayerDirectoryPicker from "@/screens/PlayerDirectoryPicker";

export default function SetupTab({ event, currentDay, updateEventLocal, onDeleteTrip, canEdit = true }) {
  const [pName, setPName] = useState("");
  const [pHcp, setPHcp] = useState("");
  const [saveToDirectory, setSaveToDirectory] = useState(true);
  const [groupSizePref, setGroupSizePref] = useState(4);
  const [editingCourse, setEditingCourse] = useState(null);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [librarySeed, setLibrarySeed] = useState(null); // { name, subtitle, par, si } to prefill save form
  const [newCourseForDay, setNewCourseForDay] = useState(false);
  const [newCourseName, setNewCourseName] = useState("");
  const [newCourseSubtitle, setNewCourseSubtitle] = useState("");
  const [pendingDayCourse, setPendingDayCourse] = useState(false); // when true, auto-set new course on current day

  const addPlayer = async () => {
    const nm = pName.trim();
    if (!nm) { toast.error("Name required"); return; }
    if ((event.players?.length || 0) >= 20) { toast.error("Max 20 players"); return; }
    const hcp = parseFloat(pHcp) || 0;
    let profileId = null;
    if (saveToDirectory) {
      try { profileId = (await createProfile({ name: nm, nickname: "", handicap: hcp })).id; }
      catch (e) { console.error("directory save failed", e); toast.error("Added to trip, but could not save to directory"); }
    }
    updateEventLocal((ev) => {
      ev.players = [...(ev.players || []), { id: uid(), name: nm, handicap: hcp, ...(profileId ? { profileId } : {}) }];
      return ev;
    });
    setPName(""); setPHcp("");
  };

  const addFromDirectory = (p) => {
    if ((event.players?.length || 0) >= 20) { toast.error("Max 20 players"); return; }
    if ((event.players || []).some((x) => x.profileId === p.id)) return;
    updateEventLocal((ev) => {
      ev.players = [...(ev.players || []), { id: uid(), name: p.name, nickname: p.nickname || "", handicap: p.handicap || 0, profileId: p.id }];
      return ev;
    });
    toast.success(`${p.nickname || p.name} added`);
  };

  const [syncing, setSyncing] = useState(false);
  const syncFromDirectory = async () => {
    const linked = (event.players || []).filter((p) => p.profileId);
    if (linked.length === 0) { toast.info("No players linked to the directory yet"); return; }
    setSyncing(true);
    try {
      const all = await searchProfiles("");
      const byId = Object.fromEntries(all.map((x) => [x.id, x]));
      let changed = 0;
      updateEventLocal((ev) => {
        ev.players = ev.players.map((p) => {
          const prof = p.profileId && byId[p.profileId];
          if (!prof) return p;
          const next = { ...p, name: prof.name, nickname: prof.nickname || "", handicap: prof.handicap ?? p.handicap };
          if (next.name !== p.name || next.nickname !== (p.nickname || "") || next.handicap !== p.handicap) changed++;
          return next;
        });
        return ev;
      });
      toast.success(changed ? `Updated ${changed} player${changed === 1 ? "" : "s"} from the directory` : "Players already match the directory");
    } catch { toast.error("Directory unavailable"); } finally { setSyncing(false); }
  };

  const doShuffle = () => {
    if (!currentDay) return;
    const ids = (event.players || []).map((p) => p.id);
    if (ids.length < 2) { toast.error("Need at least 2 players"); return; }
    const groups = autoGroups(shuffleIds(ids), groupSizePref);
    updateEventLocal((ev) => {
      ev.days = ev.days.map((d) => d.id === currentDay.id ? { ...d, groups } : d);
      return ev;
    });
    toast.success("Groups shuffled");
  };

  const removePlayer = (id) => {
    if (!window.confirm("Remove player?")) return;
    updateEventLocal((ev) => {
      ev.players = ev.players.filter((p) => p.id !== id);
      // Remove from all group assignments
      ev.days = (ev.days || []).map((d) => {
        d.groups = (d.groups || []).map((g) => ({
          ...g,
          playerIds: (g.playerIds || []).filter((pid) => pid !== id),
          scores: Object.fromEntries(Object.entries(g.scores || {}).filter(([k]) => k !== id)),
        }));
        return d;
      });
      return ev;
    });
  };

  const updatePlayer = (id, field, val) => {
    updateEventLocal((ev) => {
      ev.players = ev.players.map((p) => p.id === id ? { ...p, [field]: field === "handicap" ? (parseFloat(val) || 0) : val } : p);
      return ev;
    });
  };

  const setDayField = (field, val) => {
    if (!currentDay) return;
    updateEventLocal((ev) => {
      ev.days = ev.days.map((d) => d.id === currentDay.id ? { ...d, [field]: val } : d);
      return ev;
    });
  };

  const doAutoAssign = () => {
    if (!currentDay) return;
    const ids = (event.players || []).map((p) => p.id);
    if (ids.length < 2) { toast.error("Need at least 2 players"); return; }
    const groups = autoGroups(ids, groupSizePref);
    updateEventLocal((ev) => {
      ev.days = ev.days.map((d) => d.id === currentDay.id ? { ...d, groups } : d);
      return ev;
    });
    toast.success(`Assigned ${ids.length} players into ${groups.length} groups`);
  };

  const clearGroups = () => {
    if (!currentDay) return;
    updateEventLocal((ev) => {
      ev.days = ev.days.map((d) => d.id === currentDay.id ? { ...d, groups: [] } : d);
      return ev;
    });
  };

  const movePlayerToGroup = (playerId, targetIdx) => {
    if (!currentDay) return;
    updateEventLocal((ev) => {
      ev.days = ev.days.map((d) => {
        if (d.id !== currentDay.id) return d;
        // Ensure groups exist up to targetIdx
        let groups = [...(d.groups || [])];
        while (groups.length <= targetIdx) {
          groups.push({ playerIds: [], scores: {}, bbb: Array.from({ length: 18 }, () => ({ bingo: null, bango: null, bongo: null })) });
        }
        // Remove player from all groups
        groups = groups.map((g) => ({ ...g, playerIds: g.playerIds.filter((pid) => pid !== playerId) }));
        // Add to target
        const tg = groups[targetIdx];
        if (tg.playerIds.length >= 4) { toast.error("Group is full (max 4)"); return d; }
        tg.playerIds = [...tg.playerIds, playerId];
        return { ...d, groups };
      });
      return ev;
    });
  };

  const deleteDay = () => {
    if (!currentDay) return;
    if (!window.confirm(`Delete ${currentDay.label}?`)) return;
    updateEventLocal((ev) => {
      ev.days = ev.days.filter((d) => d.id !== currentDay.id);
      return ev;
    });
  };

  const addCourse = ({ name, subtitle = "", setAsDay = false } = {}) => {
    const nm = (name || "").trim();
    if (!nm) { toast.error("Course name required"); return null; }
    const key = nm.toLowerCase().replace(/[^a-z0-9]+/g, "").slice(0, 12) + uid().slice(0, 4);
    updateEventLocal((ev) => {
      ev.courses = { ...ev.courses, [key]: { name: nm, subtitle, par: Array(18).fill(4), si: Array.from({ length: 18 }, (_, i) => i + 1) } };
      if (setAsDay && currentDay) {
        ev.days = ev.days.map((d) => d.id === currentDay.id ? { ...d, courseKey: key } : d);
      }
      return ev;
    });
    setEditingCourse(key);
    return key;
  };

  const submitNewCourseInline = () => {
    const nm = (newCourseName || "").trim();
    const sub = (newCourseSubtitle || "").trim();
    const key = addCourse({ name: nm, subtitle: sub, setAsDay: pendingDayCourse });
    if (key) {
      const savedName = nm;
      const savedSubtitle = sub;
      toast.success(`Added "${savedName}"${pendingDayCourse ? ` to ${currentDay?.label}` : ""}`, {
        description: "Keep this course for future trips?",
        duration: 10000,
        action: {
          label: "Save to library",
          onClick: async () => {
            try {
              await createLibraryCourse({
                name: savedName,
                subtitle: savedSubtitle,
                teeBoxes: [{ id: uid(), name: "Default", par: Array(18).fill(4), si: Array.from({ length: 18 }, (_, i) => i + 1) }],
              });
              toast.success(`Saved "${savedName}" to library`);
            } catch {
              toast.error("Could not save to library");
            }
          },
        },
      });
      setNewCourseForDay(false);
      setNewCourseName("");
      setNewCourseSubtitle("");
      setPendingDayCourse(false);
    }
  };

  const openNewCourseForm = (asDayCourse = false) => {
    setNewCourseName("");
    setNewCourseSubtitle("");
    setPendingDayCourse(asDayCourse);
    setNewCourseForDay(true);
  };

  const removeCourse = (key) => {
    const usedIn = (event.days || []).filter((d) => d.courseKey === key).map((d) => d.label);
    if (usedIn.length > 0) { toast.error(`In use on ${usedIn.join(", ")}`); return; }
    if (Object.keys(event.courses || {}).length <= 1) { toast.error("Keep at least one course"); return; }
    if (!window.confirm("Remove course from this trip?")) return;
    updateEventLocal((ev) => {
      const { [key]: _removed, ...rest } = ev.courses || {};
      ev.courses = rest;
      return ev;
    });
  };

  const saveCourseToLibrary = (key) => {
    const c = event.courses[key];
    if (!c) return;
    setLibrarySeed({ name: c.name, subtitle: c.subtitle || "", par: c.par, si: c.si });
    setLibraryOpen(true);
  };

  const onImportFromLibrary = (entry) => {
    // Add course to trip.courses under a new key
    const key = (entry.name || "course").toLowerCase().replace(/[^a-z0-9]+/g, "").slice(0, 12) + uid().slice(0, 4);
    updateEventLocal((ev) => {
      ev.courses = { ...ev.courses, [key]: entry };
      if (pendingDayCourse && currentDay) {
        ev.days = ev.days.map((d) => d.id === currentDay.id ? { ...d, courseKey: key } : d);
      }
      return ev;
    });
    toast.success(`Added "${entry.name}" to trip${pendingDayCourse ? ` (${currentDay?.label})` : ""}`);
    setEditingCourse(key);
    setLibrarySeed(null);
    setPendingDayCourse(false);
  };

  const updateCourseField = (key, holeIdx, field, val) => {
    updateEventLocal((ev) => {
      const c = { ...ev.courses[key] };
      const arr = [...c[field]];
      arr[holeIdx] = parseInt(val) || 0;
      c[field] = arr;
      ev.courses = { ...ev.courses, [key]: c };
      return ev;
    });
  };

  const players = event.players || [];
  const dayPlayers = currentDay ? players.filter((p) => (currentDay.groups || []).some((g) => g.playerIds.includes(p.id)) || typeof currentDay.handicaps?.[p.id] === "number") : [];
  const dayHcpDiffers = dayPlayers.some((p) => dayHandicap(currentDay, p) !== (p.handicap || 0));
  const applyCurrentHandicaps = () => {
    if (!window.confirm(`Re-score ${currentDay.label} with everyone's current handicap?`)) return;
    updateEventLocal((ev) => {
      const handicaps = Object.fromEntries(ev.players.map((p) => [p.id, p.handicap || 0]));
      ev.days = ev.days.map((d) => d.id === currentDay.id ? { ...d, handicaps } : d);
      return ev;
    });
    toast.success(`${currentDay.label} now uses current handicaps`);
  };
  const unassignedIds = currentDay
    ? players.map((p) => p.id).filter((id) => !(currentDay.groups || []).some((g) => g.playerIds.includes(id)))
    : players.map((p) => p.id);

  return (
    <div className="space-y-5 tga-fade">
      {!canEdit && (
        <div className="tga-card-dark border-[color:var(--brass)] flex items-start gap-3">
          <div className="text-[color:var(--brass-bright)] pt-0.5">🔒</div>
          <div className="flex-1">
            <div className="font-display text-lg text-[color:var(--brass-bright)]">Setup locked</div>
            <p className="helptext text-[color:var(--muted-green)]">Only the trip creator can change players, courses and days. You can still enter scores from the Score tab.</p>
          </div>
        </div>
      )}
      <div className={canEdit ? "" : "opacity-70 pointer-events-none select-none"}>
      {/* Trip settings */}
      <div className="tga-card">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-display text-lg">Trip currency</div>
            <div className="helptext">Used for Skins and Nassau payouts</div>
          </div>
          <div className="pill-toggle">
            {["$", "£", "€", "A$", "¥"].map((sym) => (
              <button
                key={sym}
                data-testid={`setup-currency-${sym}`}
                className={(event.currency || "$") === sym ? "on" : ""}
                onClick={() => updateEventLocal((ev) => { ev.currency = sym; return ev; })}
              >{sym}</button>
            ))}
          </div>
        </div>
      </div>

      {/* Players */}
      <div className="tga-card">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-xl">Players <span className="text-[color:var(--brass-dark)] text-sm ml-1">({players.length}/20)</span></h3>
          {players.some((p) => p.profileId) && (
            <button data-testid="setup-sync-directory-btn" disabled={syncing} className="ghost-btn rounded-lg px-3 py-1 text-xs inline-flex items-center gap-1" title="Pull latest nicknames & handicaps from the directory" onClick={syncFromDirectory}>
              <RefreshCw size={12} className={syncing ? "animate-spin" : ""} /> Sync
            </button>
          )}
        </div>
        <p className="helptext text-[color:var(--text-dark)]/60 mb-2">Nicknames appear on leaderboards, results sheets and shared messages. Handicap changes apply to new days only — played days keep the handicap they started with.</p>
        <div className="space-y-2">
          {players.map((p) => (
            <div key={p.id} className="tga-row px-1 py-1.5 rounded-lg space-y-1.5">
              <div className="flex items-center gap-2">
                <div className="avatar shrink-0" title={p.profileId ? "Directory player" : ""}>{initials(p.nickname || p.name)}</div>
                <input
                  data-testid={`setup-player-name-${p.id}`}
                  className="tga-input-light flex-1 min-w-0"
                  style={{ color: 'var(--text-dark)' }}
                  value={p.name}
                  placeholder="Full name"
                  onChange={(e) => updatePlayer(p.id, "name", e.target.value)}
                />
                <button data-testid={`setup-player-remove-${p.id}`} className="danger-btn rounded-lg p-2 shrink-0" onClick={() => removePlayer(p.id)}><Trash2 size={14} /></button>
              </div>
              <div className="flex items-center gap-2 pl-[42px]">
                <input
                  data-testid={`setup-player-nick-${p.id}`}
                  className="tga-input-light flex-1 min-w-0 !py-1 text-sm"
                  style={{ color: 'var(--text-dark)' }}
                  value={p.nickname || ""}
                  placeholder="Nickname"
                  onChange={(e) => updatePlayer(p.id, "nickname", e.target.value)}
                />
                <div className="flex items-center gap-1 shrink-0">
                  <span className="text-[10px] uppercase text-[color:var(--brass-dark)]">HCP</span>
                  <input data-testid={`setup-player-hcp-${p.id}`} type="number" step="0.1" className="tga-input-light w-16 text-center !py-1 text-sm" value={p.handicap} onChange={(e) => updatePlayer(p.id, "handicap", e.target.value)} placeholder="HCP" />
                </div>
              </div>
            </div>
          ))}
          {players.length < 20 && (
            <div className="pt-2 border-t border-[rgba(27,51,34,0.1)] space-y-2">
              <PlayerDirectoryPicker
                existingProfileIds={new Set(players.map((p) => p.profileId).filter(Boolean))}
                onPick={addFromDirectory}
                disabled={!canEdit}
              />
              <div className="flex items-center gap-2">
                <input data-testid="setup-newplayer-name" className="tga-input-light flex-1" placeholder="Or add a new player name" value={pName} onChange={(e) => setPName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") addPlayer(); }} />
                <input data-testid="setup-newplayer-hcp" type="number" step="0.1" className="tga-input-light w-20" placeholder="HCP" value={pHcp} onChange={(e) => setPHcp(e.target.value)} />
                <button data-testid="setup-add-player-btn" className="brass-btn rounded-lg p-2" onClick={addPlayer}><UserPlus size={16} /></button>
              </div>
              <label className="inline-flex items-center gap-2 text-xs text-[color:var(--text-dark)]/80 cursor-pointer">
                <input data-testid="setup-save-to-directory" type="checkbox" checked={saveToDirectory} onChange={(e) => setSaveToDirectory(e.target.checked)} />
                Also save new players to the shared directory
              </label>
            </div>
          )}
        </div>
      </div>

      {/* Day setup */}
      {currentDay ? (
        <div className="tga-card">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display text-xl">{currentDay.label} setup</h3>
            <button data-testid="setup-delete-day-btn" className="danger-btn rounded-lg px-2 py-1 text-xs" onClick={deleteDay}>Delete day</button>
          </div>

          <label className="block text-xs uppercase tracking-wide text-[color:var(--brass-dark)] mb-1">Course</label>
          <div className="flex gap-2 items-stretch">
            <select data-testid="setup-course-select" className="tga-input-light flex-1" value={currentDay.courseKey} onChange={(e) => setDayField("courseKey", e.target.value)}>
              {Object.entries(event.courses || {}).map(([key, c]) => (
                <option key={key} value={key}>{c.name}</option>
              ))}
            </select>
            <button data-testid="setup-day-add-course-btn" className="ghost-btn rounded-lg px-2 text-xs inline-flex items-center gap-1" title="Add a brand new course" onClick={() => openNewCourseForm(true)}>
              <Plus size={12}/> New
            </button>
            <button data-testid="setup-day-library-btn" className="ghost-btn rounded-lg px-2 text-xs inline-flex items-center gap-1" title="Import from library" onClick={() => { setLibrarySeed(null); setPendingDayCourse(true); setLibraryOpen(true); }}>
              <BookOpen size={12}/> Library
            </button>
          </div>

          {/* Per-day handicap snapshot */}
          <div className="mt-3" data-testid="setup-day-handicaps">
            <div className="flex items-center justify-between mb-1">
              <label className="block text-xs uppercase tracking-wide text-[color:var(--brass-dark)]">Handicaps this day</label>
              {!currentDay.finalized && dayHcpDiffers && (
                <button data-testid="setup-day-handicaps-refresh" className="ghost-btn rounded-lg px-2.5 py-1 text-[11px] inline-flex items-center gap-1" onClick={applyCurrentHandicaps}>
                  <RefreshCw size={11} /> Use current handicaps
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {dayPlayers.length === 0 && <span className="helptext text-[color:var(--text-dark)]/60">Assign groups below.</span>}
              {dayPlayers.map((p) => {
                const h = dayHandicap(currentDay, p);
                const changed = h !== (p.handicap || 0);
                return (
                  <span key={p.id} data-testid={`setup-day-hcp-${p.id}`} className={`text-[11px] rounded-full px-2 py-0.5 border font-mono-metrics ${changed ? "border-amber-500/60 bg-amber-50 text-amber-900" : "border-[rgba(27,51,34,0.15)]"}`}>
                    <span className="font-sans">{p.name}</span> {h}{changed ? ` (now ${p.handicap})` : ""}
                  </span>
                );
              })}
            </div>
            <p className="helptext text-[color:var(--text-dark)]/60 mt-1">{currentDay.finalized ? "Locked — this day is finalized." : "Locked in when the day is created or first scored, so later handicap edits don't rewrite results."}</p>
          </div>

          <div className="mt-3">
            <label className="block text-xs uppercase tracking-wide text-[color:var(--brass-dark)] mb-1">Game format</label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { id: "stableford", label: "Stableford" },
                { id: "par", label: "Par" },
                { id: "stroke", label: "Stroke" },
                { id: "parPlus", label: "Par Plus" },
              ].map((f) => {
                const active = (currentDay.format || "stableford") === f.id;
                return (
                  <button
                    key={f.id}
                    data-testid={`setup-format-${f.id}`}
                    onClick={() => setDayField("format", f.id)}
                    className="text-sm rounded-lg px-2 py-1.5 border transition"
                    style={{
                      borderColor: active ? "var(--brass)" : "rgba(27,51,34,0.15)",
                      background: active ? "var(--brass)" : "transparent",
                      color: active ? "#14251b" : "var(--text-dark)",
                      fontWeight: active ? 600 : 400,
                    }}
                  >{f.label}</button>
                );
              })}
            </div>
          </div>

          <div className="mt-3 flex items-center justify-between">
            <div>
              <div className="text-sm font-medium">Bingo · Bango · Bongo</div>
              <div className="helptext">Side game points per hole</div>
            </div>
            <div className="pill-toggle">
              <button className={currentDay.bbbEnabled ? "on" : ""} onClick={() => setDayField("bbbEnabled", true)} data-testid="setup-bbb-on">On</button>
              <button className={!currentDay.bbbEnabled ? "on" : ""} onClick={() => setDayField("bbbEnabled", false)} data-testid="setup-bbb-off">Off</button>
            </div>
          </div>

          <div className="mt-3 flex items-center justify-between">
            <div>
              <div className="text-sm font-medium">Skins</div>
              <div className="helptext">Lowest score wins the hole; ties carry over</div>
            </div>
            <div className="pill-toggle">
              <button data-testid="setup-skins-off" className={(currentDay.skinsMode || "off") === "off" ? "on" : ""} onClick={() => setDayField("skinsMode", "off")}>Off</button>
              <button data-testid="setup-skins-gross" className={currentDay.skinsMode === "gross" ? "on" : ""} onClick={() => setDayField("skinsMode", "gross")}>Gross</button>
              <button data-testid="setup-skins-net" className={currentDay.skinsMode === "net" ? "on" : ""} onClick={() => setDayField("skinsMode", "net")}>Net</button>
            </div>
          </div>

          {currentDay.skinsMode && currentDay.skinsMode !== "off" && (
            <div className="mt-2 flex items-center gap-2">
              <label className="text-xs text-[color:var(--text-dark)]/70 flex-1">Value per skin</label>
              <div className="flex items-center gap-1 border border-[rgba(27,51,34,0.2)] rounded-lg px-2 py-1 bg-[rgba(27,51,34,0.03)]">
                <span className="font-mono-metrics text-sm text-[color:var(--brass-dark)]">{event.currency || "$"}</span>
                <input
                  data-testid="setup-skins-value"
                  type="number"
                  min="0"
                  step="1"
                  className="tga-input-light !border-0 !p-0 !py-0 w-20 text-right font-mono-metrics bg-transparent"
                  value={currentDay.skinsValue ?? 0}
                  onChange={(e) => setDayField("skinsValue", Math.max(0, parseFloat(e.target.value) || 0))}
                />
              </div>
            </div>
          )}

          <div className="mt-3 flex items-center justify-between">
            <div>
              <div className="text-sm font-medium">Nassau</div>
              <div className="helptext">3 bets · Front 9 · Back 9 · Overall</div>
            </div>
            <div className="pill-toggle">
              <button data-testid="setup-nassau-off" className={(currentDay.nassauMode || "off") === "off" ? "on" : ""} onClick={() => setDayField("nassauMode", "off")}>Off</button>
              <button data-testid="setup-nassau-gross" className={currentDay.nassauMode === "gross" ? "on" : ""} onClick={() => setDayField("nassauMode", "gross")}>Gross</button>
              <button data-testid="setup-nassau-net" className={currentDay.nassauMode === "net" ? "on" : ""} onClick={() => setDayField("nassauMode", "net")}>Net</button>
            </div>
          </div>

          {currentDay.nassauMode && currentDay.nassauMode !== "off" && (
            <div className="mt-2 flex items-center gap-2">
              <label className="text-xs text-[color:var(--text-dark)]/70 flex-1">Value per segment</label>
              <div className="flex items-center gap-1 border border-[rgba(27,51,34,0.2)] rounded-lg px-2 py-1 bg-[rgba(27,51,34,0.03)]">
                <span className="font-mono-metrics text-sm text-[color:var(--brass-dark)]">{event.currency || "$"}</span>
                <input
                  data-testid="setup-nassau-value"
                  type="number"
                  min="0"
                  step="1"
                  className="tga-input-light !border-0 !p-0 !py-0 w-20 text-right font-mono-metrics bg-transparent"
                  value={currentDay.nassauValue ?? 0}
                  onChange={(e) => setDayField("nassauValue", Math.max(0, parseFloat(e.target.value) || 0))}
                />
              </div>
            </div>
          )}

          {/* Groups */}
          <div className="mt-5">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
              <div className="font-medium">Playing groups</div>
              <div className="flex flex-wrap items-center gap-2">
                <label className="text-xs text-[color:var(--text-dark)]/70">Size</label>
                <select data-testid="setup-groupsize-select" className="tga-input-light !w-16 !py-1" value={groupSizePref} onChange={(e) => setGroupSizePref(parseInt(e.target.value))}>
                  {[2,3,4].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
                <button data-testid="setup-auto-assign-btn" className="brass-btn rounded-lg px-3 py-1 text-xs inline-flex items-center gap-1" onClick={doAutoAssign}>
                  <Wand2 size={14} /> Auto
                </button>
                <button data-testid="setup-shuffle-groups-btn" className="ghost-btn rounded-lg px-3 py-1 text-xs inline-flex items-center gap-1" title="Fresh random pairing" onClick={doShuffle}>
                  <Shuffle size={14} /> Shuffle
                </button>
                <button data-testid="setup-clear-groups-btn" className="ghost-btn rounded-lg px-3 py-1 text-xs" onClick={clearGroups}>Clear</button>
              </div>
            </div>

            {(currentDay.groups || []).length === 0 ? (
              <p className="helptext">No groups yet. Use Auto or drag players in below.</p>
            ) : (
              <div className="space-y-2">
                {currentDay.groups.map((g, gi) => (
                  <div key={gi} className="border border-[rgba(27,51,34,0.15)] rounded-xl p-3">
                    <div className="text-xs uppercase text-[color:var(--brass-dark)] mb-2">Group {String.fromCharCode(65 + gi)} ({g.playerIds.length}/4)</div>
                    <div className="flex flex-wrap gap-2">
                      {g.playerIds.map((pid) => {
                        const p = players.find((x) => x.id === pid);
                        if (!p) return null;
                        return (
                          <div key={pid} className="inline-flex items-center gap-2 bg-[rgba(27,51,34,0.06)] rounded-full pl-1 pr-2 py-1">
                            <div className="avatar" style={{ width: 26, height: 26, fontSize: 12 }}>{initials(p.name)}</div>
                            <span className="text-sm">{p.nickname || p.name}</span>
                            <button
                              data-testid={`setup-remove-from-group-${pid}`}
                              className="text-[color:var(--brass-dark)] hover:text-red-700 text-xs"
                              onClick={() => movePlayerToGroup(pid, -1)}
                              aria-label="Remove"
                            >✕</button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {unassignedIds.length > 0 && (
              <div className="mt-3 border-t border-dashed border-[rgba(27,51,34,0.2)] pt-3">
                <div className="text-xs uppercase text-[color:var(--brass-dark)] mb-2">Unassigned</div>
                <div className="flex flex-wrap gap-2">
                  {unassignedIds.map((pid) => {
                    const p = players.find((x) => x.id === pid);
                    if (!p) return null;
                    return (
                      <div key={pid} className="inline-flex items-center gap-2 bg-[rgba(27,51,34,0.03)] border border-dashed border-[rgba(27,51,34,0.2)] rounded-full pl-1 pr-2 py-1">
                        <div className="avatar" style={{ width: 26, height: 26, fontSize: 12 }}>{initials(p.nickname || p.name)}</div>
                        <span className="text-sm">{p.nickname || p.name}</span>
                        <select
                          data-testid={`setup-assign-select-${pid}`}
                          className="text-xs bg-transparent border border-[rgba(27,51,34,0.25)] rounded px-1 py-0.5"
                          value=""
                          onChange={(e) => { if (e.target.value !== "") movePlayerToGroup(pid, parseInt(e.target.value)); }}
                        >
                          <option value="">Add to…</option>
                          {(currentDay.groups || []).map((_, i) => <option key={i} value={i}>Group {String.fromCharCode(65 + i)}</option>)}
                          <option value={(currentDay.groups || []).length}>+ New group</option>
                        </select>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="tga-card-dark text-center py-8">
          <p className="helptext mb-3">Add a day to start scoring.</p>
        </div>
      )}

      {/* Courses */}
      <div className="tga-card">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-xl">Courses</h3>
          <div className="flex items-center gap-2">
            <button data-testid="setup-open-library-btn" className="ghost-btn rounded-lg px-3 py-1 text-xs inline-flex items-center gap-1" onClick={() => { setLibrarySeed(null); setPendingDayCourse(false); setLibraryOpen(true); }}>
              <BookOpen size={14}/> Library
            </button>
            <button data-testid="setup-add-course-btn" className="brass-btn rounded-lg px-3 py-1 text-xs inline-flex items-center gap-1" onClick={() => openNewCourseForm(false)}><Plus size={14}/> New</button>
          </div>
        </div>

        {newCourseForDay && (
          <div className="mb-3 p-3 rounded-lg border border-dashed border-[color:var(--brass-dark)] bg-[rgba(197,160,89,0.05)]">
            <div className="text-xs uppercase text-[color:var(--brass-dark)] mb-2">
              {pendingDayCourse ? `New course for ${currentDay?.label}` : "New course"}
            </div>
            <input
              data-testid="setup-newcourse-name"
              className="tga-input-light w-full mb-2"
              placeholder="Course name"
              autoFocus
              value={newCourseName}
              onChange={(e) => setNewCourseName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") submitNewCourseInline(); }}
            />
            <input
              data-testid="setup-newcourse-subtitle"
              className="tga-input-light w-full mb-2"
              placeholder="Subtitle (optional, e.g. Club · City)"
              value={newCourseSubtitle}
              onChange={(e) => setNewCourseSubtitle(e.target.value)}
            />
            <div className="flex gap-2">
              <button data-testid="setup-newcourse-save-btn" className="brass-btn rounded-lg px-3 py-1 text-sm" onClick={submitNewCourseInline}>Add course</button>
              <button className="ghost-btn rounded-lg px-3 py-1 text-sm" onClick={() => { setNewCourseForDay(false); setPendingDayCourse(false); }}>Cancel</button>
            </div>
            <p className="helptext text-[color:var(--text-dark)]/60 mt-2">Set Par and Stroke Index per hole below after adding.</p>
          </div>
        )}
        <div className="space-y-2">
          {Object.entries(event.courses || {}).map(([key, c]) => (
            <div key={key} className="border border-[rgba(27,51,34,0.15)] rounded-lg">
              <div className="w-full flex items-center gap-1 px-2 py-2">
                <button className="flex-1 text-left flex items-center gap-2" data-testid={`setup-course-toggle-${key}`} onClick={() => setEditingCourse(editingCourse === key ? null : key)}>
                  <div className="flex-1">
                    <div className="font-medium">{c.name}</div>
                    {c.subtitle && <div className="text-xs opacity-70">{c.subtitle}</div>}
                    {c.libraryId && <div className="text-[10px] uppercase tracking-wide text-[color:var(--brass-dark)]" data-testid={`setup-course-linked-${key}`}>Linked to library</div>}
                  </div>
                  <ChevronsUpDown size={14} />
                </button>
                <button data-testid={`setup-course-save-lib-${key}`} className="ghost-btn rounded-md p-1 text-xs" title="Save to library" onClick={() => saveCourseToLibrary(key)}>
                  <Save size={13} />
                </button>
                <button data-testid={`setup-course-remove-${key}`} className="danger-btn rounded-md p-1" title="Remove from trip" onClick={() => removeCourse(key)}>
                  <Trash2 size={13} />
                </button>
              </div>
              {editingCourse === key && (
                <div className="px-3 pb-3">
                  <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 font-mono-metrics text-xs">
                    {c.par.map((par, i) => (
                      <div key={i} className="border border-[rgba(27,51,34,0.15)] rounded p-1 text-center">
                        <div className="text-[10px] uppercase opacity-70">H{i + 1}</div>
                        <input data-testid={`course-par-${key}-${i}`} type="number" min="3" max="6" className="tga-input-light w-full !p-1 text-center" value={par} onChange={(e) => updateCourseField(key, i, "par", e.target.value)} />
                        <input data-testid={`course-si-${key}-${i}`} type="number" min="1" max="18" className="tga-input-light w-full !p-1 text-center mt-1" value={c.si[i]} onChange={(e) => updateCourseField(key, i, "si", e.target.value)} />
                      </div>
                    ))}
                  </div>
                  <p className="helptext mt-2 text-[color:var(--text-dark)]/60">Top = Par · Bottom = Stroke Index</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <CourseLibraryModal
        open={libraryOpen}
        onClose={() => { setLibraryOpen(false); setLibrarySeed(null); setPendingDayCourse(false); }}
        onImport={onImportFromLibrary}
        initialSaveCourse={librarySeed}
        updateEventLocal={updateEventLocal}
      />

      <div className="pt-2">
        <button data-testid="setup-delete-trip-btn" className="danger-btn rounded-lg w-full py-2 text-sm" onClick={onDeleteTrip}>Delete this trip</button>
      </div>
      </div>
    </div>
  );
}
